import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { DURATION, fadeInUp, staggerContainer } from "@/lib/animations";
import { apiRequest } from "@/lib/queryClient";
import { zodResolver } from "@hookform/resolvers/zod";
import { insertContactSchema, type InsertContact } from "@shared/schema";
import { useMutation } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
  Facebook,
  Linkedin,
  Mail,
  MapPin,
  Phone,
  Send
} from "lucide-react";
import { useForm } from "react-hook-form";

const contactInfo = [
  {
    icon: Mail,
    titleKey: "emailUs",
    details: ["info@Ibtkary.net"],
  },
  {
    icon: Phone,
    titleKey: "callUs",
    details: ["+201068338151"],
  },
  {
    icon: MapPin,
    titleKey: "visitUs",
    detailsKeys: ["locationDetails1"],
  },
];

const socialLinks = [
  { icon: Linkedin, href: "https://www.linkedin.com/company/ibtkary/", label: "LinkedIn" },
  { icon: Facebook, href: "https://www.facebook.com/ibtkary/", label: "Facebook" },
];

export default function Contact() {
  const { toast } = useToast();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';

  const form = useForm<InsertContact>({
    resolver: zodResolver(insertContactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      projectType: "",
      message: "",
    },
  });

  const contactMutation = useMutation({
    mutationFn: async (data: InsertContact) => {
      return apiRequest("POST", "/api/contact", data);
    },
    onSuccess: () => {
      toast({
        title: t("messageSentSuccess"),
        description: t("messageSentDescription"),
      });
      form.reset();
    },
    onError: (error) => {
      toast({
        title: t("messageFailedTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: InsertContact) => {
    contactMutation.mutate(data);
  };

  return (
    <section id="contact" className="py-24 bg-background" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            {t("contactTitle")} <span className="gradient-text">{t("contactTitleHighlight")}</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t("contactDescription")}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: DURATION.slow }}
            viewport={{ once: true }}
          >
            <Card className="bg-card border border-border/50">
              <CardContent className="p-6 md:p-8">
                <h3 className={`text-xl font-semibold mb-6 ${isRTL ? 'text-right' : ''}`}>
                  {t("getInTouch")}
                </h3>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    <div className="grid md:grid-cols-2 gap-5">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={isRTL ? 'text-right block' : ''}>
                              {t("fullName")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder={t("namePlaceholder")}
                                className="h-11"
                                {...field}
                                dir={isRTL ? 'rtl' : 'ltr'}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className={isRTL ? 'text-right block' : ''}>
                              {t("emailAddress")}
                            </FormLabel>
                            <FormControl>
                              <Input
                                type="email"
                                placeholder={t("emailPlaceholder")}
                                className="h-11"
                                {...field}
                                dir={isRTL ? 'rtl' : 'ltr'}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="phone"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isRTL ? 'text-right block' : ''}>
                            {t("phoneNumber")}
                          </FormLabel>
                          <FormControl>
                            <Input
                              type="tel"
                              placeholder={t("phonePlaceholder")}
                              className="h-11"
                              {...field}
                              dir={isRTL ? 'rtl' : 'ltr'}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="projectType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isRTL ? 'text-right block' : ''}>
                            {t("projectType")}
                          </FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger className="h-11" dir={isRTL ? 'rtl' : 'ltr'}>
                                <SelectValue placeholder={t("selectService")} />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent dir={isRTL ? 'rtl' : 'ltr'}>
                              <SelectItem value="mobile">{t("mobile")}</SelectItem>
                              <SelectItem value="web">{t("web")}</SelectItem>
                              <SelectItem value="backend">{t("backend")}</SelectItem>
                              <SelectItem value="ai">{t("ai")}</SelectItem>
                              <SelectItem value="design">{t("design")}</SelectItem>
                              <SelectItem value="consulting">{t("consultingShort")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className={isRTL ? 'text-right block' : ''}>
                            {t("message")}
                          </FormLabel>
                          <FormControl>
                            <Textarea
                              rows={4}
                              placeholder={t("messagePlaceholder")}
                              className="resize-none"
                              {...field}
                              dir={isRTL ? 'rtl' : 'ltr'}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      disabled={contactMutation.isPending}
                      size="lg"
                      className="w-full"
                    >
                      {contactMutation.isPending ? t("sending") : t("sendMessage")}
                      <Send className={`h-4 w-4 ${isRTL ? 'mr-2' : 'ml-2'}`} />
                    </Button>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            className="space-y-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {contactInfo.map((info) => (
              <motion.div
                key={info.titleKey}
                variants={fadeInUp}
                className="p-5 rounded-xl bg-card border border-border/50 card-hover"
              >
                <div
                  className="flex items-start gap-4"
                  style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <info.icon className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">
                      {t(info.titleKey)}
                    </h4>
                    {info.detailsKeys ? (
                      info.detailsKeys.map((key, idx) => (
                        <p key={idx} className="text-muted-foreground text-sm">
                          {t(key)}
                        </p>
                      ))
                    ) : (
                      info.details?.map((detail, idx) => (
                        <p key={idx} className="text-muted-foreground text-sm" dir="ltr">
                          {detail}
                        </p>
                      ))
                    )}
                  </div>
                </div>
              </motion.div>
            ))}

            {/* Social Links */}
            <motion.div
              variants={fadeInUp}
              className="pt-4"
            >
              <h4 className={`font-semibold text-foreground mb-4 ${isRTL ? 'text-right' : ''}`}>
                {t("followUs")}
              </h4>
              <div className="flex gap-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
                {socialLinks.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center text-primary hover:bg-primary hover:text-primary-foreground transition-colors duration-200"
                    aria-label={social.label}
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
